import axios, { AxiosInstance } from 'axios';
import JsonApi, { Response } from 'devour-client';
import { JsonApiPayload } from './interfaces/JsonApiPayload';
import { ServiceBase } from './jsonapi/ServiceBase';

export class VehicleLookupService extends ServiceBase<VehicleLookupPayload> {
  cached: VehicleParts<VehicleLookupPayload[]>;
  _rawAxios: AxiosInstance;

  constructor(jsonApi: JsonApi) {
    const schema = VehicleLookupPayload.getSchema();
    super(jsonApi, 'vehicle_fake', schema, VehicleLookupPayload);
  }

  getElectricals = async () => {
    let entities: VehicleLookupPayload[] = await this.makeGet('vehicle_electricals');
    return entities;
  }

  getTypes = async () => {
    let entities: VehicleLookupPayload[] = await this.makeGet('vehicle_types?filter[active]=true');
    return entities;
  }

  getLengthRanges = async () => {
    let entities: VehicleLookupPayload[] = await this.makeGet('vehicle_length_ranges');
    return entities;
  }

  getSlides = async () => {
    let entities: VehicleLookupPayload[] = await this.makeGet('vehicle_slides');
    return entities;
  }

  getTowings = async () => {
    let entities: VehicleLookupPayload[] = await this.makeGet('vehicle_towings');
    return entities;
  }

  getLookups = async () => {
    if (this.cached) {
      return this.cached;
    }

    const requests = {
      types: this.getTypes(),
      lengthRanges: this.getLengthRanges(),
      slides: this.getSlides(),
      towings: this.getTowings(),
      electricals: this.getElectricals(),
    };  
    const resultsArr = await Promise.all(Object.values(requests));
    const results = Object.fromEntries(Object.keys(requests).map((key, idx) => [key, resultsArr[idx]]));
    const { types, lengthRanges, slides, towings, electricals } = results;
    const lookups = { types, lengthRanges, slides, towings, electricals };
    this.cached = lookups;

    return lookups;
  }

  getMaps = async () => {
    const results = await this.getLookups();
    const maps = convertAllToMap(results);
    return maps;
  }

  get rawAxios() {
    if (!this._rawAxios) {
      this._rawAxios = axios.create({
        baseURL: this.jsonApi.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.jsonApi.bearer}`,
        }
      });
    }
    return this._rawAxios;
  }

  private makeGet = async (url: string) => {
    const response = await this.rawAxios.get<Response>(url);
    const data = response.data.data;
    if (Array.isArray(data)) {
      const entities = data.map<any>(x => ({ id: x.id, ...x.attributes }));
      return entities;
    }
  }
}

type VehicleParts<T> = {
  types: T;
  lengthRanges: T;
  slides: T;
  towings: T;
  electricals: T;
}

export type VehicleMaps = VehicleParts<Map<string, string>>;

const convertAllToMap = async (values: VehicleParts<VehicleLookupPayload[]>) => {
  const keys = Object.keys(values);
  const entries = keys.map(key => {
    const item = values[key];
    const map = new Map(item.map(x => (
      [ x.id, x.label]
    )));
    return [key, map];
  });
  
  const result = Object.fromEntries(entries) as VehicleMaps;
  return result;
};

export class VehicleLookupPayload extends JsonApiPayload {
  id: string;
  abbr: string;
  label: string;
  description: string;

  static getSchema() {
    return super._getSchema(this, []);
  }
}
